import * as T from 'three';

// Optional background for the standalone screen. It shares the avatar's
// renderer and never changes the head, particle geometry, or pointer input.
const screenVertex = /* glsl */`
    varying vec2 vUv;
    void main() {
        vUv = uv;
        gl_Position = vec4(position.xy, 1.0, 1.0);
    }
`;

const cloudFragment = /* glsl */`
    precision highp float;
    varying vec2 vUv;
    uniform float uPhase;
    uniform float uAspect;
    uniform vec3 uBlue;

    float hash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
    }
    float noise(vec2 p) {
        vec2 i = floor(p), f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
                   mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0)), f.x), f.y);
    }
    float fbm(vec2 p) {
        float sum = 0.0, amplitude = 0.5;
        mat2 turn = mat2(0.8, -0.6, 0.6, 0.8);
        for (int i = 0; i < 5; i++) {
            sum += amplitude * noise(p);
            p = turn * p * 2.03 + vec2(7.3, 11.8);
            amplitude *= 0.5;
        }
        return sum;
    }
    void main() {
        vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0) * 4.6;
        // Circular noise-domain paths make both the position and velocity
        // continuous at the end of the 96-second cycle.
        vec2 flow = vec2(cos(uPhase), sin(uPhase));
        vec2 counterflow = vec2(sin(uPhase * 2.0), cos(uPhase * 2.0));
        vec2 warp = vec2(fbm(p * 0.85 + flow * 0.65),
                         fbm(p * 0.85 - flow * 0.55 + vec2(4.1, 9.7))) - 0.5;
        float density = fbm(p + warp * 2.8 + flow * 0.75);
        float fine = fbm(p * 2.4 + warp * 1.5 + counterflow * 0.28);
        float clouds = smoothstep(0.30, 0.76, density);
        float wisps = pow(1.0 - abs(density * 2.0 - 1.0), 5.0)
                    * smoothstep(0.34, 0.78, fine);
        float vignette = 1.0 - smoothstep(0.35, 0.76, length((vUv - 0.5) * vec2(0.9, 1.0)));
        vec2 center = (vUv - vec2(0.5, 0.53)) * vec2(uAspect, 1.0);
        float quietCenter = 1.0 - 0.62 * exp(-dot(center, center) * 25.0);
        vec3 blue = uBlue * (0.014 + clouds * 0.25 + wisps * 0.10);
        // Encode the intermediate 8-bit texture perceptually to prevent
        // visible banding in dark blue. Decode before the final sRGB output.
        vec3 encoded = pow(max(blue * vignette * quietCenter, vec3(0.0)), vec3(1.0 / 2.2));
        encoded += (hash(gl_FragCoord.xy) - 0.5) / 255.0;
        gl_FragColor = vec4(max(encoded, vec3(0.0)), 1.0);
    }
`;

export class NebulaBackground {
    readonly period = 96;
    readonly mesh: T.Mesh;
    private geometry = new T.PlaneGeometry(2, 2);
    private target = new T.WebGLRenderTarget(1, 1, {
        depthBuffer: false, stencilBuffer: false,
        minFilter: T.LinearFilter, magFilter: T.LinearFilter
    });
    private cloudMaterial: T.ShaderMaterial;
    private displayMaterial: T.ShaderMaterial;
    private cloudScene = new T.Scene();
    private camera = new T.Camera();
    private previous = 0;
    private painted = -Infinity;
    private elapsed = 0;
    private dirty = true;
    private disposed = false;

    constructor(private scene: T.Scene, blue: T.Color) {
        this.cloudMaterial = new T.ShaderMaterial({
            vertexShader: screenVertex, fragmentShader: cloudFragment,
            uniforms: { uPhase: { value: 0 }, uAspect: { value: 1 }, uBlue: { value: blue.clone() } },
            depthTest: false, depthWrite: false, toneMapped: false
        });
        const cloud = new T.Mesh(this.geometry, this.cloudMaterial);
        cloud.frustumCulled = false;
        this.cloudScene.add(cloud);
        this.displayMaterial = new T.ShaderMaterial({
            vertexShader: screenVertex,
            fragmentShader: /* glsl */`
                varying vec2 vUv;
                uniform sampler2D uCloud;
                void main() {
                    gl_FragColor = vec4(pow(texture2D(uCloud, vUv).rgb, vec3(2.2)), 1.0);
                    #include <colorspace_fragment>
                    gl_FragColor.rgb *= 0.5;
                }
            `,
            uniforms: { uCloud: { value: this.target.texture } },
            depthTest: false, depthWrite: false, toneMapped: false
        });
        this.mesh = new T.Mesh(this.geometry, this.displayMaterial);
        this.mesh.name = 'MobileNebulaBackground';
        this.mesh.frustumCulled = false;
        this.mesh.renderOrder = -100;
        scene.add(this.mesh);
    }

    resize(width: number, height: number) {
        const aspect = width / Math.max(1, height);
        // The soft clouds need no full-DPR render; preserve mobile GPU time.
        const scale = Math.min(0.6, 640 / Math.max(width, height));
        this.target.setSize(Math.max(1, Math.round(width * scale)), Math.max(1, Math.round(height * scale)));
        this.cloudMaterial.uniforms.uAspect.value = aspect;
        this.dirty = true;
    }

    update(renderer: T.WebGLRenderer, now: number, reduced = false) {
        if (this.disposed || document.hidden) return;
        if (this.previous && !reduced) this.elapsed += Math.min(0.1, Math.max(0, (now - this.previous) / 1000));
        this.previous = now;
        if (!this.dirty && (reduced || now - this.painted < 1000 / 24)) return;
        this.cloudMaterial.uniforms.uPhase.value = (this.elapsed % this.period) / this.period * Math.PI * 2;
        const target = renderer.getRenderTarget();
        const cubeFace = renderer.getActiveCubeFace(), mip = renderer.getActiveMipmapLevel();
        try {
            renderer.setRenderTarget(this.target);
            renderer.render(this.cloudScene, this.camera);
        } finally {
            renderer.setRenderTarget(target, cubeFace, mip);
        }
        this.painted = now;
        this.dirty = false;
    }

    pause() { this.previous = 0; }
    dispose() {
        if (this.disposed) return;
        this.disposed = true;
        this.scene.remove(this.mesh);
        this.cloudScene.clear();
        this.geometry.dispose();
        this.cloudMaterial.dispose();
        this.displayMaterial.dispose();
        this.target.dispose();
    }
}
